import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Mail, User, Building, MessageSquare, Send,
  CheckCircle, AlertCircle, Loader2, MapPin
} from 'lucide-react';
import ParticleBackground from '../components/ParticleBackground';
import AnimatedBackground from '../components/AnimatedBackground';
import GlowingCard from '../components/GlowingCard';
import { submitContactForm } from '../lib/api';
import { useForm } from '../hooks/useForm';

export default function ContactPage() {
  const {
    values,
    errors,
    isSubmitting,
    submitError,
    submitSuccess,
    handleChange,
    handleSubmit,
    reset,
  } = useForm(
    { name: '', email: '', company: '', subject: '', message: '' },
    submitContactForm
  );

  const validationRules = {
    name: { required: true, message: 'Please enter your name' },
    email: { required: true, email: true },
    subject: { required: true, message: 'Please select a subject' },
    message: { required: true, minLength: 10 },
  };

  const subjects = [
    'General Inquiry',
    'Sales & Pricing',
    'Technical Support',
    'Partnership Opportunity',
    'Media & Press',
    'Careers',
    'Other',
  ];

  if (submitSuccess) {
    return (
      <div className="relative min-h-screen pt-20 flex items-center justify-center">
        <AnimatedBackground variant="gaming" />
        <ParticleBackground density={20} />

        <div className="max-w-lg mx-auto px-6 py-24 text-center">
          <GlowingCard glowColor="green">
            <div className="p-12">
              <div className="w-20 h-20 rounded-full bg-green-500/20 border border-green-500/30 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-400" />
              </div>
              <h1 className="text-3xl font-bold text-white mb-4">Message Sent!</h1>
              <p className="text-slate-400 mb-8">
                Thank you for reaching out. Our team will get back to you within 24-48 hours.
              </p>
              <div className="flex gap-4 justify-center">
                <button
                  onClick={reset}
                  className="px-6 py-3 rounded-xl font-semibold border border-slate-600 text-white hover:border-blue-500/50 transition-all"
                >
                  Send Another Message
                </button>
                <Link
                  to="/"
                  className="px-6 py-3 rounded-xl font-semibold bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white transition-all"
                >
                  Back to Home
                </Link>
              </div>
            </div>
          </GlowingCard>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen pt-20">
      <AnimatedBackground variant="gaming" />
      <ParticleBackground density={15} />

      <div className="max-w-6xl mx-auto px-6 py-16">
        {/* Back Link */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div>
            <h1 className="text-4xl font-bold text-white mb-4">Get in Touch</h1>
            <p className="text-lg text-slate-400 mb-8">
              Have questions about the CAO Platform? We'd love to hear from you.
              Fill out the form and our team will get back to you shortly.
            </p>

            <GlowingCard glowColor="blue">
              <form
                onSubmit={(e) => handleSubmit(e, validationRules)}
                className="p-8 space-y-6"
              >
                {submitError && (
                  <div className="flex items-center gap-2 p-4 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400">
                    <AlertCircle className="w-5 h-5 shrink-0" />
                    <p>{submitError}</p>
                  </div>
                )}

                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Full Name *
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <input
                      type="text"
                      name="name"
                      value={values.name}
                      onChange={handleChange}
                      placeholder="John Doe"
                      className={`w-full pl-10 pr-4 py-3 rounded-xl bg-slate-800/50 border ${
                        errors.name ? 'border-red-500/50' : 'border-slate-700'
                      } text-white placeholder-slate-500 focus:outline-none focus:border-blue-500/50 transition-colors`}
                    />
                  </div>
                  {errors.name && (
                    <p className="text-red-400 text-sm mt-1">{errors.name}</p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Email Address *
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <input
                      type="email"
                      name="email"
                      value={values.email}
                      onChange={handleChange}
                      placeholder="john@company.com"
                      className={`w-full pl-10 pr-4 py-3 rounded-xl bg-slate-800/50 border ${
                        errors.email ? 'border-red-500/50' : 'border-slate-700'
                      } text-white placeholder-slate-500 focus:outline-none focus:border-blue-500/50 transition-colors`}
                    />
                  </div>
                  {errors.email && (
                    <p className="text-red-400 text-sm mt-1">{errors.email}</p>
                  )}
                </div>

                {/* Company */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Company (Optional)
                  </label>
                  <div className="relative">
                    <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <input
                      type="text"
                      name="company"
                      value={values.company}
                      onChange={handleChange}
                      placeholder="Acme Inc."
                      className="w-full pl-10 pr-4 py-3 rounded-xl bg-slate-800/50 border border-slate-700 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500/50 transition-colors"
                    />
                  </div>
                </div>

                {/* Subject */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Subject *
                  </label>
                  <select
                    name="subject"
                    value={values.subject}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 rounded-xl bg-slate-800/50 border ${
                      errors.subject ? 'border-red-500/50' : 'border-slate-700'
                    } text-white focus:outline-none focus:border-blue-500/50 transition-colors`}
                  >
                    <option value="">Select a subject...</option>
                    {subjects.map((subject) => (
                      <option key={subject} value={subject}>
                        {subject}
                      </option>
                    ))}
                  </select>
                  {errors.subject && (
                    <p className="text-red-400 text-sm mt-1">{errors.subject}</p>
                  )}
                </div>

                {/* Message */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Message *
                  </label>
                  <div className="relative">
                    <MessageSquare className="absolute left-3 top-3 w-5 h-5 text-slate-500" />
                    <textarea
                      name="message"
                      value={values.message}
                      onChange={handleChange}
                      placeholder="Tell us how we can help..."
                      rows={5}
                      className={`w-full pl-10 pr-4 py-3 rounded-xl bg-slate-800/50 border ${
                        errors.message ? 'border-red-500/50' : 'border-slate-700'
                      } text-white placeholder-slate-500 focus:outline-none focus:border-blue-500/50 transition-colors resize-none`}
                    />
                  </div>
                  {errors.message && (
                    <p className="text-red-400 text-sm mt-1">{errors.message}</p>
                  )}
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 rounded-xl font-semibold bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            </GlowingCard>
          </div>

          {/* Contact Info */}
          <div className="space-y-8">
            <GlowingCard glowColor="purple">
              <div className="p-8">
                <h2 className="text-2xl font-bold text-white mb-6">Contact Information</h2>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-blue-500/10 border border-blue-500/30 flex items-center justify-center shrink-0">
                      <Mail className="w-6 h-6 text-blue-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white">Email</h3>
                      <p className="text-slate-400">info@caoplatform.com</p>
                      <p className="text-slate-400">support@caoplatform.com</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center shrink-0">
                      <MapPin className="w-6 h-6 text-cyan-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white">Location</h3>
                      <p className="text-slate-400">Austin, TN</p>
                      <p className="text-slate-400">United States</p>
                    </div>
                  </div>
                </div>
              </div>
            </GlowingCard>

            <GlowingCard glowColor="cyan">
              <div className="p-8">
                <h2 className="text-2xl font-bold text-white mb-4">Quick Links</h2>
                <div className="space-y-3">
                  <Link
                    to="/demo"
                    className="block p-4 rounded-lg bg-slate-800/50 border border-slate-700/50 hover:border-blue-500/50 transition-all"
                  >
                    <h3 className="font-semibold text-white">Request a Demo</h3>
                    <p className="text-sm text-slate-400">See the CAO Platform in action</p>
                  </Link>
                  <Link
                    to="/features"
                    className="block p-4 rounded-lg bg-slate-800/50 border border-slate-700/50 hover:border-blue-500/50 transition-all"
                  >
                    <h3 className="font-semibold text-white">Explore Features</h3>
                    <p className="text-sm text-slate-400">Learn about our capabilities</p>
                  </Link>
                </div>
              </div>
            </GlowingCard>
          </div>
        </div>
      </div>
    </div>
  );
}
